import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import api from "../../api/client";

/**
 * PersonnelSelect — dropdown for selecting FST/FST MIG personnel.
 *
 * Props:
 *   value      — selected user id (number | null)
 *   onChange   — (userId: number | null, userData: object | null) => void
 *   companyId  — filter personnel by company
 *   disabled   — boolean
 *   label      — placeholder text
 *   allowNone  — show a "None" option (for Personnel 2)
 *   className  — additional CSS
 *   personnelList — optional pre-fetched list (avoids duplicate API calls)
 *   disabledIds   — Set or array of user IDs that should be grayed-out / unselectable
 *   disabledReason — function (userId) => string tooltip, or a static string
 */
export default function PersonnelSelect({
  value,
  onChange,
  companyId,
  disabled = false,
  label = "— Select Personnel —",
  allowNone = false,
  className = "",
  personnelList: externalList,
  disabledIds: rawDisabledIds,
  disabledReason,
}) {
  const disabledSet = useMemo(() => {
    if (!rawDisabledIds) return new Set();
    if (rawDisabledIds instanceof Set) return rawDisabledIds;
    return new Set(rawDisabledIds);
  }, [rawDisabledIds]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const fetchPersonnel = useCallback(async () => {
    if (externalList) {
      setPersonnel(externalList);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      const { data } = await api.get("/team-masters/personnel", { params });
      setPersonnel(data);
    } catch {
      setPersonnel([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, externalList]);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedUser = personnel.find((p) => p.id === value);
  const filtered = search
    ? personnel.filter(
        (p) =>
          p.display_name.toLowerCase().includes(search.toLowerCase()) ||
          (p.full_name || "").toLowerCase().includes(search.toLowerCase())
      )
    : personnel;

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full border rounded px-3 py-2 text-sm text-left flex items-center justify-between gap-2 ${
          disabled
            ? "bg-gray-50 text-gray-400 cursor-not-allowed"
            : "bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        }`}
      >
        <span className={selectedUser || value === null ? (value === null && allowNone ? "text-gray-500" : selectedUser ? "text-gray-900" : "text-gray-400") : "text-gray-400"}>
          {loading ? "Loading..." : selectedUser ? selectedUser.display_name : value === null && allowNone ? "None" : label}
        </span>
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search personnel..."
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {allowNone && (
              <button
                type="button"
                onClick={() => { setOpen(false); setSearch(""); onChange(null, null); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${value === null ? "bg-blue-50 font-medium" : ""}`}
              >
                <span className="text-gray-500 italic">None</span>
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400 text-center">
                {loading ? "Loading..." : "No personnel found"}
              </div>
            ) : (
              filtered.map((p) => {
                const isDisabled = disabledSet.has(p.id);
                const reason =
                  isDisabled && disabledReason
                    ? typeof disabledReason === "function"
                      ? disabledReason(p.id)
                      : disabledReason
                    : null;
                return (
                <button
                  key={p.id}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => { if (!isDisabled) { setOpen(false); setSearch(""); onChange(p.id, p); } }}
                  title={reason || undefined}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed bg-gray-50"
                      : p.id === value
                      ? "bg-blue-50 font-medium hover:bg-blue-50"
                      : "hover:bg-blue-50"
                  }`}
                >
                  <span>
                    <span className={isDisabled ? "text-gray-400" : "font-medium"}>{p.display_name}</span>
                    <span className="text-gray-400 ml-2 text-xs">{p.role_name}</span>
                    {isDisabled && reason && (
                      <span className="block text-xs text-red-400 mt-0.5">{reason}</span>
                    )}
                  </span>
                  {p.id === value && !isDisabled && (
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
